import { formatISO, parseISO } from "date-fns";
import { z } from "zod";

const CalendarEventKindSchema = z.enum(["personal", "blocked"]);
const TitleSchema = z.string().max(200).nullable();
const NotesSchema = z.string().max(2000).nullable();

const CalendarEventSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  kind: CalendarEventKindSchema,
  title: TitleSchema,
  notes: NotesSchema,
  startAt: z.date(),
  endAt: z.date(),
  isAllDay: z.boolean(),
  timezone: z.string(),
  blocksAvailability: z.boolean(),
  createdBy: z.string().nullable(),
  createdAt: z.date(),
  modifiedAt: z.date(),
  version: z.number().int().positive(),
  deletedAt: z.date().nullable(),
});

const CreateCalendarEventRequestSchema = CalendarEventSchema.omit({
  id: true, timezone: true, createdBy: true, createdAt: true,
  modifiedAt: true, version: true, deletedAt: true,
}).partial({
  title: true, notes: true, isAllDay: true, blocksAvailability: true,
}).extend({ conflictToken: z.string().optional() });

const UpdateCalendarEventRequestSchema = CalendarEventSchema.pick({
  title: true, notes: true, startAt: true, endAt: true,
  isAllDay: true, blocksAvailability: true,
}).partial().extend({
  expectedVersion: z.number().int().positive(),
  conflictToken: z.string().optional(),
});

const CalendarEventConflictRequestSchema = CalendarEventSchema.pick({
  startAt: true, endAt: true, blocksAvailability: true,
}).extend({ excludeEventId: z.string().optional() });

const CalendarEventDtoSchema = z.object({
  id: z.string(),
  business_id: z.string(),
  kind: CalendarEventKindSchema,
  title: TitleSchema,
  notes: NotesSchema,
  start_at: z.string().datetime({ offset: true }),
  end_at: z.string().datetime({ offset: true }),
  is_all_day: z.boolean(),
  timezone: z.string(),
  blocks_availability: z.boolean(),
  created_by: z.string().nullable(),
  created_at: z.string().datetime({ offset: true }),
  modified_at: z.string().datetime({ offset: true }),
  version: z.number().int().positive(),
  deleted_at: z.string().datetime({ offset: true }).nullable(),
});

export const CreateCalendarEventDtoSchema = CalendarEventDtoSchema.pick({
  kind: true, title: true, notes: true, start_at: true, end_at: true,
  is_all_day: true, blocks_availability: true,
}).partial({
  title: true, notes: true, is_all_day: true, blocks_availability: true,
}).extend({ conflict_token: z.string().optional() });

export const UpdateCalendarEventDtoSchema = CalendarEventDtoSchema.pick({
  title: true, notes: true, start_at: true, end_at: true,
  is_all_day: true, blocks_availability: true,
}).partial().extend({
  expected_version: z.number().int().positive(),
  conflict_token: z.string().optional(),
});

const CalendarEventConflictRequestDtoSchema = CalendarEventDtoSchema.pick({
  start_at: true, end_at: true, blocks_availability: true,
}).extend({ exclude_event_id: z.string().optional() });

const CalendarEventConflictDtoSchema = z.object({
  appointment_id: z.string(),
  start_at: z.string().datetime({ offset: true }),
  end_at: z.string().datetime({ offset: true }),
  location_id: z.string().nullable(),
  status: z.enum(["pending", "confirmed"]),
});

const CalendarEventConflictPreviewDtoSchema = z.object({
  conflicts: z.array(CalendarEventConflictDtoSchema),
  conflict_token: z.string().nullable(),
});

const CalendarEventConflictSchema = z.object({
  appointmentId: z.string(),
  startAt: z.date(),
  endAt: z.date(),
  locationId: z.string().nullable(),
  status: z.enum(["pending", "confirmed"]),
});

const CalendarEventConflictPreviewSchema = z.object({
  conflicts: z.array(CalendarEventConflictSchema),
  conflictToken: z.string().nullable(),
});

export type CalendarEventDto = z.infer<typeof CalendarEventDtoSchema>;
export type CreateCalendarEventDto = z.infer<typeof CreateCalendarEventDtoSchema>;
export type UpdateCalendarEventDto = z.infer<typeof UpdateCalendarEventDtoSchema>;
export type CalendarEventConflictRequestDto = z.infer<typeof CalendarEventConflictRequestDtoSchema>;
export type CalendarEventConflictPreviewDto = z.infer<typeof CalendarEventConflictPreviewDtoSchema>;

export type CalendarEventKind = z.infer<typeof CalendarEventKindSchema>;
export type CalendarEvent = z.infer<typeof CalendarEventSchema>;
export type CreateCalendarEventRequest = z.infer<typeof CreateCalendarEventRequestSchema>;
export type UpdateCalendarEventRequest = z.infer<typeof UpdateCalendarEventRequestSchema>;
export type CalendarEventConflictRequest = z.infer<typeof CalendarEventConflictRequestSchema>;
export type CalendarEventConflictPreview = z.infer<typeof CalendarEventConflictPreviewSchema>;

export function fromCalendarEventDtoToCalendarEvent(dto: CalendarEventDto): CalendarEvent {
  const event = CalendarEventDtoSchema.parse(dto);
  return {
    id: event.id,
    businessId: event.business_id,
    kind: event.kind,
    title: event.title,
    notes: event.notes,
    startAt: parseISO(event.start_at),
    endAt: parseISO(event.end_at),
    isAllDay: event.is_all_day,
    timezone: event.timezone,
    blocksAvailability: event.blocks_availability,
    createdBy: event.created_by,
    createdAt: parseISO(event.created_at),
    modifiedAt: parseISO(event.modified_at),
    version: event.version,
    deletedAt: event.deleted_at ? parseISO(event.deleted_at) : null,
  };
}

export function fromCreateCalendarEventRequestToCreateCalendarEventDto(
  request: CreateCalendarEventRequest,
): CreateCalendarEventDto {
  const event = CreateCalendarEventRequestSchema.parse(request);
  return {
    kind: event.kind,
    title: event.title,
    notes: event.notes,
    start_at: formatISO(event.startAt),
    end_at: formatISO(event.endAt),
    is_all_day: event.isAllDay,
    blocks_availability: event.blocksAvailability,
    conflict_token: event.conflictToken,
  };
}

export function fromUpdateCalendarEventRequestToUpdateCalendarEventDto(
  request: UpdateCalendarEventRequest,
): UpdateCalendarEventDto {
  const event = UpdateCalendarEventRequestSchema.parse(request);
  return {
    expected_version: event.expectedVersion,
    title: event.title,
    notes: event.notes,
    start_at: event.startAt ? formatISO(event.startAt) : undefined,
    end_at: event.endAt ? formatISO(event.endAt) : undefined,
    is_all_day: event.isAllDay,
    blocks_availability: event.blocksAvailability,
    conflict_token: event.conflictToken,
  };
}

export function fromCalendarEventConflictRequestToDto(
  request: CalendarEventConflictRequest,
): CalendarEventConflictRequestDto {
  const event = CalendarEventConflictRequestSchema.parse(request);
  return CalendarEventConflictRequestDtoSchema.parse({
    start_at: formatISO(event.startAt),
    end_at: formatISO(event.endAt),
    blocks_availability: event.blocksAvailability,
    exclude_event_id: event.excludeEventId,
  });
}

export function fromCalendarEventConflictPreviewDtoToPreview(
  dto: CalendarEventConflictPreviewDto,
): CalendarEventConflictPreview {
  const preview = CalendarEventConflictPreviewDtoSchema.parse(dto);
  return CalendarEventConflictPreviewSchema.parse({
    conflicts: preview.conflicts.map((conflict) => ({
      appointmentId: conflict.appointment_id,
      startAt: parseISO(conflict.start_at),
      endAt: parseISO(conflict.end_at),
      locationId: conflict.location_id,
      status: conflict.status,
    })),
    conflictToken: preview.conflict_token,
  });
}
